import Axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from "axios";

import { apiBaseUrl } from "../config/env";
import { transformAxiosError } from "./errors";

const CARTER_INGRESS_HOSTNAME_REGEX = /(^|\.)integrationinsightengine\.(pwc|pwcinternal)\.com$/i;

function getCurrentOrigin(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const origin = window.location?.origin;
  return typeof origin === "string" && origin.trim().length > 0 ? origin : undefined;
}

function isCarterIngressHostname(hostname: string): boolean {
  return CARTER_INGRESS_HOSTNAME_REGEX.test(hostname);
}

export function getApiClientBaseUrl(baseUrl: string, currentOrigin = getCurrentOrigin()): string | undefined {
  const trimmed = baseUrl.trim();
  if (trimmed.length === 0) return undefined;
  const normalized = trimmed.replace(/\/api\/?$/, "");
  if (!normalized) return undefined;

  if (!currentOrigin) return normalized;

  try {
    const configuredUrl = new URL(normalized);
    const pageUrl = new URL(currentOrigin);
    const configuredHost = configuredUrl.hostname.toLowerCase();
    const pageHost = pageUrl.hostname.toLowerCase();

    if (
      configuredUrl.origin !== pageUrl.origin &&
      isCarterIngressHostname(configuredHost) &&
      isCarterIngressHostname(pageHost)
    ) {
      return undefined;
    }
  } catch {
    return normalized;
  }

  return normalized;
}

function isAbsoluteUrl(url: string): boolean {
  return /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(url);
}

function withApiPrefix(url: string): string {
  if (isAbsoluteUrl(url) || url === "/api" || url.startsWith("/api/")) {
    return url;
  }
  return url.startsWith("/") ? `/api${url}` : `/api/${url}`;
}

export const apiClient = Axios.create({
  baseURL: getApiClientBaseUrl(apiBaseUrl),
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
});

// ----- Auth token injection -----
/**
 * Set this from the auth layer to inject a Bearer token into requests. The
 * getter may be sync or async (MSAL acquires tokens asynchronously).
 */
let accessTokenGetter: (() => string | null | Promise<string | null>) | null = null;

export function setAccessTokenGetter(
  getter: () => string | null | Promise<string | null>
): void {
  accessTokenGetter = getter;
}

/** Optional: called when response is 401. Set from app to redirect to login / clear session. Pass null to clear. */
let onUnauthorized: (() => void) | null = null;

export function setOnUnauthorized(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/** Optional: called when response is 403. Set from app to show a consistent forbidden UX. Pass null to clear. */
let onForbidden: (() => void) | null = null;
let lastForbiddenHandledAt = 0;
const FORBIDDEN_HANDLER_COOLDOWN_MS = 3000;

export function setOnForbidden(handler: (() => void) | null): void {
  onForbidden = handler;
  lastForbiddenHandledAt = 0;
}

// ----- Request interceptor: inject auth token -----
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (typeof config.url === "string") {
      config.url = withApiPrefix(config.url);
    }
    const token = (await accessTokenGetter?.()) ?? null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ----- Response interceptor: error transformation + 401 handling -----
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<unknown>) => {
    const apiError = transformAxiosError(error);
    if (apiError.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    const now = Date.now();
    if (apiError.status === 403 && onForbidden && now - lastForbiddenHandledAt >= FORBIDDEN_HANDLER_COOLDOWN_MS) {
      lastForbiddenHandledAt = now;
      onForbidden();
    }
    return Promise.reject(apiError);
  }
);

// ----- Generic typed request helpers (return data; throw ApiError) -----
export async function request<T>(config: AxiosRequestConfig): Promise<T> {
  const { data } = await apiClient.request<T>(config);
  return data;
}

export function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request<T>({ ...config, method: "GET", url });
}

export function post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request<T>({ ...config, method: "POST", url, data });
}

export function put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request<T>({ ...config, method: "PUT", url, data });
}

export function patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
  return request<T>({ ...config, method: "PATCH", url, data });
}

export function del<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return request<T>({ ...config, method: "DELETE", url });
}

export type { AxiosError, AxiosRequestConfig };
